import React, { useState } from 'react';
import { useFormContext, Controller, get, useWatch } from 'react-hook-form';
import { ChevronDown, ChevronUp } from 'lucide-react';
import CurrencyInput from '../../../../../../components/CurrencyInput';

import { OrderSchemaType, ItemPath } from '../../order.schema';
import { useItemSubtotalCalculator } from '../../Hooks/useItemSubtotalCalculator';
import {
  CollapsibleHeader,
  CircularCollapseIcon,
  FormGroup,
  Input,
  Label,
  Separator,
  SubTitle,
  FormGrid
} from '../../style';
import { OrderItemType } from '../../types';

interface PricesSectionProps {
  itemPath: ItemPath;
  quantity: number;
}

const PricesSection: React.FC<PricesSectionProps> = ({ itemPath, quantity }) => {
  const [isPriceOpen, setIsPriceOpen] = useState(true);

  const { control, register, watch, formState: { errors } } = useFormContext<OrderSchemaType>();

  const itemType = useWatch({ control, name: `${itemPath}.itemType` });

  const {
    unitPrice,
    itemTaxes,
    itemSubtotal,
  } = useItemSubtotalCalculator({ itemPath, itemType: itemType as OrderItemType, quantity });

  const priceDetailsPath = `${itemPath}.priceDetails` as const;

  const [pricePerKgMinRaw] = watch([
    `${priceDetailsPath}.pricePerKgMin`,
  ] as const) as [number | undefined];

  const pricePerKgMin: number | undefined = typeof pricePerKgMinRaw === 'number' ? pricePerKgMinRaw : undefined;


  return (
    <>
      <Separator />
      <CollapsibleHeader onClick={() => setIsPriceOpen(prev => !prev)}>
        <SubTitle>Preço</SubTitle>
        <CircularCollapseIcon onClick={(e) => { e.stopPropagation(); setIsPriceOpen(prev => !prev); }}>
          {isPriceOpen ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
        </CircularCollapseIcon>
      </CollapsibleHeader>

      {isPriceOpen && (
        <>
          <FormGrid columns={3} style={{ marginTop: '16px' }}>

            <FormGroup>
              <Label htmlFor={`${priceDetailsPath}.discount`}>Desconto (%)</Label>
              <Input
                id={`${priceDetailsPath}.discount`}
                type="text"
                {...register(
                  `${priceDetailsPath}.discount` as const
                )}
                placeholder="Digite"
                hasError={!!get(errors, `${priceDetailsPath}.discount`)}
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor={`${priceDetailsPath}.deliveryDays`}>Prazo</Label>
              <Input
                id={`${priceDetailsPath}.deliveryDays`}
                type="text"
                {...register(
                  `${priceDetailsPath}.deliveryDays` as const
                )}
                placeholder="15 dias úteis"
                hasError={!!get(errors, `${priceDetailsPath}.deliveryDays`)}
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor={`${priceDetailsPath}.itemSubtotal`}>Subtotal</Label>
              <Controller
                name={`${priceDetailsPath}.itemSubtotal` as const}
                control={control}
                render={({ field }) => (
                  <CurrencyInput
                    id={`${priceDetailsPath}.itemSubtotal`}
                    name={`${priceDetailsPath}.itemSubtotal`}
                    value={field.value || 0}
                    onValueChange={(value, name, values) => {
                      field.onChange(values?.float || 0);
                    }}
                    placeholder="R$ 0,00"
                    hasError={!!get(errors, `${priceDetailsPath}.itemSubtotal`)}
                  />
                )}
              />
            </FormGroup>
          </FormGrid>
          <Separator />
          <FormGrid columns={3} style={{ marginTop: '16px', alignItems: 'center' }}>
            <div>
              <Label style={{ fontSize: '14px', fontWeight: 'normal' }}>Quantidade de peças:</Label>
              <span style={{ fontSize: '16px', fontWeight: 'bold', marginLeft: '8px' }}>{quantity}</span>
            </div>

            <div>
              <Label style={{ fontSize: '14px', fontWeight: 'normal' }}>Preço unitário:</Label>
              <span style={{ fontSize: '16px', fontWeight: 'bold', marginLeft: '8px' }}>
                {unitPrice.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
              </span>
            </div>

            <div>
              <Label style={{ fontSize: '14px', fontWeight: 'normal' }}>Tributos:</Label>
              <span style={{ fontSize: '16px', fontWeight: 'bold', marginLeft: '8px' }}>
                {itemTaxes.toLocaleString('pt-BR', { style: 'percent', minimumFractionDigits: 1 })}
              </span>
            </div>

          </FormGrid>
          <Separator />
        </>
      )}
    </>
  );
};

export default PricesSection;